import React, { useEffect, useState, useCallback, useRef } from "react";

import RadioCard from "../../../components/form-item/RadioCard";
import Input from "../../../components/form-item/Input";
import TitleBlock from "../../../components/block/TitleBlock";
import SvgLoadingAnimation from "../../../components/loading/index";
import StatusIndicator from "../../../components/status-indicator/StatusIndicator";
import { useGetCurrentUserQuery, useUpdateCurrentUserMutation } from "../../../app/accounts/authuser";

const Notification = () => {
  const [formData, setFormData] = useState({
    notification_setting: "now",
    notification_time: "10:00",
  });
  const [updateStatus, setUpdateStatus] = useState("idle"); // "idle" | "saving" | "saved" | "error"
  const debounceTimeoutRef = useRef(null);

  // RTK Query hooks
  const { data: userData, error, isLoading } = useGetCurrentUserQuery();
  const [updateCurrentUser] = useUpdateCurrentUserMutation();

  // サーバーからのデータを状態に反映
  useEffect(() => {
    if (userData?.data) {
      const { notification_setting, notification_time } = userData.data;
      setFormData({
        notification_setting: notification_setting || "now",
        notification_time: notification_time || "10:00",
      });
    }
  }, [userData]);

  // デバウンス付きリアルタイム更新機能
  const debouncedUpdate = useCallback(async (fieldName, fieldValue) => {
    try {
      setUpdateStatus("saving");
      await updateCurrentUser({ [fieldName]: fieldValue }).unwrap();
      setUpdateStatus("saved");
      // 1秒後にsavedステータスをクリア
      setTimeout(() => setUpdateStatus("idle"), 1000);
    } catch (error) {
      console.error("Failed to update notification settings:", error);
      setUpdateStatus("error");
      setTimeout(() => setUpdateStatus("idle"), 3000);
    }
  }, [updateCurrentUser]);

  // ラジオボタンの変更処理
  const handleRadioChange = (newSetting) => {
    setFormData(prev => ({ ...prev, notification_setting: newSetting }));
    
    // 既存のタイマーをクリア
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // 500ms後に更新実行
    debounceTimeoutRef.current = setTimeout(() => {
      debouncedUpdate("notification_setting", newSetting);
    }, 500);
  };

  // 時刻入力の変更処理
  const handleTimeChange = (newTime) => {
    setFormData(prev => ({ ...prev, notification_time: newTime }));
    
    // 既存のタイマーをクリア
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // 500ms後に更新実行
    debounceTimeoutRef.current = setTimeout(() => {
      debouncedUpdate("notification_time", newTime);
    }, 500);
  };

  // クリーンアップ処理
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // ローディング状態の表示
  if (isLoading) {
    return <SvgLoadingAnimation />;
  }

  // エラー状態の表示
  if (error) {
    return <div>データの取得に失敗しました</div>;
  }

  return (
    <>
      <TitleBlock
        title="通知"
        description="メッセージごとのメール配信タイミングを設定"
      />
  
      <div className="u-col u-gap16">
        <RadioCard
          name="notification_setting"
          id="now"
          width="128px"
          height="128px"
          showRadio
          checked={formData.notification_setting === "now"}
          showImage
          onClick={() => handleRadioChange("now")}
        >
          <label>今すぐ</label>
          <p className="bodytext-13">15分ごとに配信</p>
        </RadioCard>

        <RadioCard
          name="notification_setting"
          id="on"
          width="128px"
          height="128px"
          showRadio
          checked={formData.notification_setting === "on"}
          showImage
          onClick={() => handleRadioChange("on")}
        >
          <label className="u-w100">1日1回まとめて配信</label>
          <Input
            label=""
            type="time"
            value={formData.notification_time}
            width="112px"
            prepend="配信時間"
            onChange={(e) => handleTimeChange(e.target.value)}
          />
        </RadioCard>

        <RadioCard
          name="notification_setting"
          id="off"
          width="128px"
          height="128px"
          showRadio
          checked={formData.notification_setting === "off"}
          showImage
          onClick={() => handleRadioChange("off")}
        >
          <label>オフ</label>
          <p className="bodytext-13">配信なし</p>
        </RadioCard>
      </div>
          
      {/* 更新状態の表示 */}
      <StatusIndicator status={updateStatus} />
    </>
  );
};

export default Notification;
